import React, { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { useSelector } from "react-redux";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";
import {
	createNotification,
	CustomDialog,
	CustomTextField,
	HorizontalDivider,
	icon,
	KnowledgeBasePopOver,
	LimitedTextField,
	Selector,
	VerticalDivider,
} from "../../../../../../common/components";
import styles from "./AgentCreator.module.css";
import { translate } from "../../../../../../common/providers";
import { AGENT_PATTERNS } from "../../constants";
import { useApi } from "../../../../../../common/hooks";
import { AiAgentsService } from "../../../../../../api";
import { getModelSelectorItems, highlightText, mapAgentModelError } from "../../utils";

const STREAMING_TYPES = { PER_CONTENT: "PER_CONTENT", PER_PAGE: "PER_PAGE" };

export default function AgentCreator({ agent = null, open, onClose, onRefresh }) {
	const [name, setName] = useState("");
	const [tokenLimitState, setTokenLimitState] = useState(undefined);
	const [toolLimitState, setToolLimitState] = useState(undefined);
	const [prompt, setPrompt] = useState("");
	const [accessPattern, setAccessPattern] = useState(AGENT_PATTERNS.AUTOMATIC);
	const [openConfirmation, setOpenConfirmation] = useState(false);
	const [streamingSize, setStreamingSize] = useState(null);
	const [streamingType, setStreamingType] = useState(null);
	const [isPromptInputActive, setIsPromptInputActive] = useState(false);
	const [models, setModels] = useState([]);
	const [selectedModelId, setSelectedModelId] = useState(null);
	const textareaRef = useRef(null);
	const deactivateTimeoutRef = useRef(null);
	const isPromptInputActiveRef = useRef(false);
	const promptFieldRef = useRef(null);
	const overlayRef = useRef(null);
	const [textareaRect, setTextareaRect] = useState(null);
	const [relativePos, setRelativePos] = useState({ top: 0, left: 0 });
	const userId = useSelector(({ context }) => context?.user?.id);
	const { call: createAgentDefinition } = useApi(AiAgentsService.createAgentDefinition);
	const { call: updateAgentDefinition } = useApi(AiAgentsService.updateAgentDefinition);
	const { call: getModelList } = useApi(AiAgentsService.getModelList);
	const modelItems = useMemo(() => getModelSelectorItems(models), [models]);
	const selectedModel = useMemo(
		() => modelItems.find((item) => (item?.id ?? null) === (selectedModelId ?? null)) || null,
		[modelItems, selectedModelId]
	);
	const isValidForm = useMemo(
		() =>
			(accessPattern !== "STREAM_ALL_PARAGRAPHS_BY_DBPAGES" ||
				(streamingSize !== null && streamingSize > 0 && streamingType !== null)) &&
			name?.length > 0 &&
			prompt?.length > 0 &&
			accessPattern,
		[name, prompt, accessPattern, streamingSize, streamingType]
	);

	useEffect(() => {
		isPromptInputActiveRef.current = isPromptInputActive;
	}, [isPromptInputActive]);

	useEffect(() => {
		const cancelDeactivation = () => {
			clearTimeout(deactivateTimeoutRef.current);
			deactivateTimeoutRef.current = null;
		};
		const refocusIfActive = () => {
			cancelDeactivation();
			if (isPromptInputActiveRef.current && textareaRef.current) {
				textareaRef.current.focus();
			}
		};
		const onVisibilityChange = () => {
			if (document.hidden) {
				cancelDeactivation();
			} else {
				refocusIfActive();
			}
		};
		window.addEventListener("blur", cancelDeactivation);
		window.addEventListener("focus", refocusIfActive);
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => {
			window.removeEventListener("blur", cancelDeactivation);
			window.removeEventListener("focus", refocusIfActive);
			document.removeEventListener("visibilitychange", onVisibilityChange);
		};
	}, []);

	useEffect(() => {
		const suppressResizeObserverLoopError = (e) => {
			if (e.message === "ResizeObserver loop completed with undelivered notifications.") {
				e.stopImmediatePropagation();
			}
		};
		window.addEventListener("error", suppressResizeObserverLoopError);
		return () => window.removeEventListener("error", suppressResizeObserverLoopError);
	}, []);

	useEffect(() => {
		if (!isPromptInputActive || !textareaRef.current || !promptFieldRef.current) {
			setTextareaRect(null);
			return undefined;
		}
		const updateRect = () => {
			requestAnimationFrame(() => {
				if (!textareaRef.current || !promptFieldRef.current) {
					return;
				}
				const textRect = textareaRef.current.getBoundingClientRect();
				const parentRect = promptFieldRef.current.getBoundingClientRect();
				const computedStyle = window.getComputedStyle(textareaRef.current);
				setTextareaRect({
					width: textareaRef.current.clientWidth,
					height: textRect.height,
					fontSize: computedStyle.fontSize,
					fontFamily: computedStyle.fontFamily,
					fontWeight: computedStyle.fontWeight,
					lineHeight: computedStyle.lineHeight,
					letterSpacing: computedStyle.letterSpacing,
				});
				setRelativePos({
					top: textRect.top - parentRect.top + promptFieldRef.current.scrollTop,
					left: textRect.left - parentRect.left,
				});
			});
		};
		updateRect();
		const resizeObserver = new ResizeObserver(updateRect);
		resizeObserver.observe(textareaRef.current);
		const textareaNode = textareaRef.current;
		const syncOverlayScroll = () => {
			if (overlayRef.current) {
				overlayRef.current.scrollTop = textareaNode.scrollTop;
			}
		};
		textareaNode.addEventListener("scroll", syncOverlayScroll);
		return () => {
			resizeObserver.disconnect();
			textareaNode.removeEventListener("scroll", syncOverlayScroll);
		};
	}, [isPromptInputActive]);

	useEffect(() => {
		if (!open) {
			return;
		}

		getModelList({ active: true, page: 0, size: 200, sort: "name;asc" })
			.then((response) => {
				setModels(Array.isArray(response?.contents) ? response.contents : []);
			})
			.catch(console.error);
	}, [open, getModelList]);

	useEffect(() => {
		if (!open) {
			return;
		}

		if (!agent) {
			setName("");
			setTokenLimitState(null);
			setToolLimitState(null);
			setPrompt("");
			setAccessPattern(AGENT_PATTERNS.AUTOMATIC);
			setStreamingSize(null);
			setStreamingType(null);
			setSelectedModelId(null);
			return;
		}

		const {
			title,
			prompt: agentPrompt,
			pattern,
			tokenLimit,
			toolLimit,
			streamingSize: agentStreamingSize,
			streamingType: agentStreamingType,
			modelId,
			usesDefaultModel,
		} = agent;

		setName(title);
		setTokenLimitState(tokenLimit);
		setToolLimitState(toolLimit);
		setPrompt(agentPrompt);
		setAccessPattern(pattern);
		setStreamingSize(agentStreamingSize);
		setStreamingType(agentStreamingType);
		setSelectedModelId(usesDefaultModel || modelId == null ? null : modelId);
	}, [agent, open]);

	const formatNumber = (value) => {
		if (!value) {
			return "";
		}
		return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
	};

	const handleChangeTokenLimit = (newTokenLimit) => {
		const stripped = newTokenLimit.replace(/\s/g, "");
		if (/^\d*$/.test(stripped)) {
			setTokenLimitState(stripped);
		}
	};

	const handleChangeToolLimit = (newToolLimit) => {
		if (/^\d*$/.test(newToolLimit)) {
			setToolLimitState(newToolLimit);
		}
	};

	const handleChangePrompt = (newPrompt) => {
		if (!isPromptInputActive) {
			setIsPromptInputActive(true);
		}
		setPrompt(newPrompt);
	};

	const handleSelectPattern = (newPattern) => {
		if (accessPattern !== "STREAM_ALL_PARAGRAPHS_BY_DBPAGES") {
			setStreamingSize(null);
			setStreamingType(null);
		}
		setAccessPattern(newPattern);
	};

	const selectorItemRenderer = (label) => <span className={styles.itemRow}>{label}</span>;
	const modelItemRenderer = (item) => <span className={styles.itemRow}>{item?.label || item?.name || ""}</span>;

	const resetState = () => {
		setName("");
		setPrompt("");
		setTokenLimitState(null);
		setToolLimitState(null);
		setStreamingType(null);
		setStreamingSize(null);
		setSelectedModelId(null);
		setAccessPattern(AGENT_PATTERNS.AUTOMATIC);
		setOpenConfirmation(false);
		setIsPromptInputActive(false);
	};

	const handleChangeName = (newName) => setName(newName);

	const handleDefinitiveClose = () => {
		resetState();
		onClose();
	};

	const handleClose = () => {
		const currentModelId = selectedModelId ?? null;
		const initialModelId = agent?.usesDefaultModel || agent?.modelId == null ? null : agent?.modelId;
		const anyChange =
			(!agent && (accessPattern || name?.length > 0 || prompt?.length > 0)) ||
			(agent &&
				(name !== agent.title ||
					prompt !== agent.prompt ||
					accessPattern !== agent.pattern ||
					tokenLimitState !== agent.tokenLimit ||
					toolLimitState !== agent.toolLimit ||
					streamingSize !== agent.streamingSize ||
					streamingType !== agent.streamingType ||
					currentModelId !== initialModelId));
		if (anyChange) {
			setOpenConfirmation(true);
		} else {
			handleDefinitiveClose();
		}
	};

	const handleSubmit = () => {
		if (!userId) {
			createNotification({ type: "error", message: translate("canvas.error-network") });
			return;
		}
		if (isValidForm) {
			const request = (agent && updateAgentDefinition) || createAgentDefinition;
			const payload = {
				title: name,
				prompt,
				tokenLimit: parseInt(tokenLimitState, 10) || undefined,
				toolLimit: parseInt(toolLimitState, 10) || undefined,
				pattern: accessPattern,
				creatorId: userId,
				streamingSize: accessPattern !== "STREAM_ALL_PARAGRAPHS_BY_DBPAGES" ? null : streamingSize,
				streamingType: accessPattern !== "STREAM_ALL_PARAGRAPHS_BY_DBPAGES" ? null : streamingType,
				modelId: selectedModelId ?? null,
			};
			const params = (agent && [{ agentId: agent?.id }, payload]) || [payload];
			request(...params)
				.then(() => {
					onRefresh();
					handleDefinitiveClose();
				})
				.catch((error) => {
					console.error(error);
					createNotification({
						type: "error",
						message: mapAgentModelError(error) || translate("canvas.error-network"),
					});
				});
		}
	};

	const handleChangeStreamingSize = (e) => {
		setStreamingSize(e.target.value);
	};

	const handleChangeStreamingType = (e) => {
		setStreamingType(e.target.value);
	};

	const handleActivateInput = () => {
		setIsPromptInputActive(true);
	};
	const handleDeactivateInput = () => {
		deactivateTimeoutRef.current = setTimeout(() => {
			deactivateTimeoutRef.current = null;
			setIsPromptInputActive(false);
		}, 0);
	};
	return (
		<CustomDialog
			disableBackdropClick
			disableEscapeKeyDown
			displayCloseCross
			isForm
			className={styles.dialog}
			classNameDialogHeader={styles.dialogHeader}
			classNameMainDialog={styles.dialogMain}
			open={!!open}
			Paperprops={{ sx: { width: "80vw", maxHeight: "92vh", maxWidth: "none", overflowY: "auto" } }}
			submitDisabled={!isValidForm}
			submitLabel={translate("common:btn.validate")}
			title={translate(`dsi.agents.dialog.title.${(!!agent && "modification") || "creation"}`)}
			onClose={handleClose}
			onSubmit={handleSubmit}
		>
			<div className={styles.dialogContent}>
				<div className={styles.fieldWrapper}>
					<div className={styles.fieldLabel}>
						{translate("common:field.name")}
						<span className={styles.required}>{translate("dsi.agents.requirement-filed")}</span>
					</div>
					<CustomTextField
						fullWidth
						required
						className={styles.textfieldContainer__input}
						value={name}
						variant="outlined"
						onChange={(e) => handleChangeName(e.target.value)}
					/>
				</div>
				<HorizontalDivider className={styles.horizontalDivider} />
				<div className={styles.promptWrapper}>
					<div className={styles.fieldLabel}>
						{translate("dsi.agents.prompt-label")}
						<KnowledgeBasePopOver
							color="var(--grey-500)"
							label={translate("dsi.agents.prompt-tooltip")}
							size="xs"
						/>
						<span className={styles.required}>{translate("dsi.agents.requirement-filed")}</span>
					</div>
					<div ref={promptFieldRef} className={styles.promptField}>
						{((isPromptInputActive || !prompt) && (
							<>
								<LimitedTextField
									fullWidth
									multiline
									autoFocus={isPromptInputActive}
									className={styles.textField}
									InputProps={{
										style: { height: "100%" },
										inputRef: textareaRef,
									}}
									maxLength={16383}
									showHelper={false}
									value={prompt}
									variant="outlined"
									onBlur={handleDeactivateInput}
									onChange={handleChangePrompt}
								/>
								{textareaRect && (
									<div
										ref={overlayRef}
										className={styles.overlay}
										// eslint-disable-next-line react/no-danger
										dangerouslySetInnerHTML={{
											__html:
												DOMPurify.sanitize(prompt?.length > 0 && highlightText(prompt)) || null,
										}}
										style={{
											width: textareaRect.width,
											height: textareaRect.height,
											top: relativePos.top,
											left: relativePos.left,
											fontSize: textareaRect.fontSize,
											fontFamily: textareaRect.fontFamily,
											fontWeight: textareaRect.fontWeight,
											lineHeight: textareaRect.lineHeight,
											letterSpacing: textareaRect.letterSpacing,
										}}
									/>
								)}
							</>
						)) || (
							<div
								role="presentation"
								// eslint-disable-next-line react/jsx-sort-props
								onClick={handleActivateInput}
								className={styles.promptInput}
								// eslint-disable-next-line react/no-danger
								dangerouslySetInnerHTML={{
									__html: DOMPurify.sanitize(prompt?.length > 0 && highlightText(prompt)) || null,
								}}
							/>
						)}
					</div>
				</div>
				<HorizontalDivider className={styles.horizontalDivider} />
				<div className={styles.formRow}>
					<div className={styles.formField}>
						<div className={styles.fieldLabel}>
							{translate("dsi.agents.access-model")}
							<KnowledgeBasePopOver
								color="var(--color-light-grey-1)"
								label={translate("dsi.agents.access-model-tooltip")}
								size="xs"
							/>
							<span className={styles.required}>{translate("dsi.agents.requirement-filed")}</span>
						</div>
						<Selector
							fullWidth
							field="name"
							itemRenderer={selectorItemRenderer}
							items={modelItems}
							placeholder="dsi.agents.dialog.ai-model.placeholder"
							value={selectedModel || ""}
							valueRenderer={modelItemRenderer}
							onChange={(e) => setSelectedModelId(e.target.value?.id ?? null)}
						/>
					</div>
					<VerticalDivider className={styles.verticalDivider} />
					<div className={styles.formField}>
						<div className={styles.fieldLabel}>
							{translate("dsi.agents.access-pattern")}
							<KnowledgeBasePopOver
								color="var(--grey-500)"
								label={translate("dsi.agents.access-pattern-tooltip")}
								size="xs"
							/>
							<span className={styles.required}>{translate("dsi.agents.requirement-filed")}</span>
						</div>
						<Selector
							fullWidth
							itemRenderer={selectorItemRenderer}
							items={Object.values(AGENT_PATTERNS)}
							value={accessPattern}
							valueRenderer={selectorItemRenderer}
							onChange={(e) => handleSelectPattern(e.target.value)}
						/>
					</div>
				</div>
				{accessPattern === "STREAM_ALL_PARAGRAPHS_BY_DBPAGES" && (
					<>
						<HorizontalDivider className={styles.horizontalDivider} />
						<div className={styles.formRow__extend}>
							<div className={styles.additionalContainer__title}>
								<div className={styles.additionalContainer__desp}>
									{translate("dsi.agents.dialog.title.additional-properties.title")}
									<span className={styles.required}>{translate("dsi.agents.requirement-filed")}</span>
								</div>
								<RadioGroup
									aria-labelledby="demo-radio-buttons-group-label"
									defaultValue="female"
									name="radio-buttons-group"
									value={streamingType}
									onChange={handleChangeStreamingType}
								>
									<FormControlLabel
										className={styles.radioRow__container}
										control={<Radio className={styles.radioRow__radio} />}
										label={
											<span>
												<div className={styles.radioRow__name}>
													{translate(
														"dsi.agents.dialog.title.additional-properties.radio.content.title"
													)}
												</div>
												<div className={styles.radioRow__helper}>
													{translate(
														"dsi.agents.dialog.title.additional-properties.radio.content.helper"
													)}
												</div>
											</span>
										}
										value={STREAMING_TYPES.PER_CONTENT}
									/>
									<FormControlLabel
										className={styles.radioRow__container}
										control={<Radio className={styles.radioRow__radio} />}
										label={
											<span>
												<div className={styles.radioRow__name}>
													{translate(
														"dsi.agents.dialog.title.additional-properties.radio.pages.title"
													)}
												</div>
												<div className={styles.radioRow__helper}>
													{translate(
														"dsi.agents.dialog.title.additional-properties.radio.pages.helper"
													)}
												</div>
											</span>
										}
										value={STREAMING_TYPES.PER_PAGE}
									/>
								</RadioGroup>
							</div>
							<div className={styles.additionalContainer__batch}>
								{translate("dsi.agents.dialog.title.additional-properties.batch-size")}
								<div className={styles.textfieldContainer}>
									<CustomTextField
										fullWidth
										className={styles.textfieldContainer__textfield}
										placeholder={translate(
											"dsi.agents.dialog.title.additional-properties.textfield-placeholder"
										)}
										type="number"
										value={streamingSize}
										onChange={handleChangeStreamingSize}
									/>
									<span className={styles.lowerCase}>
										{translate(
											(streamingType === STREAMING_TYPES.PER_PAGE &&
												"dsi.agents.dialog.title.additional-properties.pages") ||
												"dsi.agents.dialog.title.additional-properties.contents"
										)}
									</span>
								</div>
								<div
									className={`${styles.radioRow__helper} ${
										(streamingSize < 0 && styles["radioRow__helper--error"]) || ""
									}`}
								>
									{translate("dsi.agents.dialog.title.additional-properties.error")}
								</div>
							</div>
						</div>
					</>
				)}
				<HorizontalDivider className={styles.horizontalDivider} />
				<div className={styles.formRow}>
					<div className={styles.formField__left}>
						<div className={styles.fieldLabel}>
							{translate("dsi.agents.dialog.title.token-limit")}
							<KnowledgeBasePopOver
								color="var(--grey-500)"
								label={translate("dsi.agents.dialog.title.token-limit-tooltip")}
								size="xs"
							/>
						</div>
						<CustomTextField
							fullWidth
							className={styles.textfieldContainer__input}
							type="text"
							value={formatNumber(tokenLimitState)}
							variant="outlined"
							onChange={(e) => handleChangeTokenLimit(e.target.value)}
						/>
					</div>
					<VerticalDivider className={styles.verticalDivider} />
					<div className={styles.formField}>
						<div className={styles.fieldLabel}>
							{translate("dsi.agents.dialog.title.tool-limit")}
							<KnowledgeBasePopOver
								color="var(--grey-500)"
								label={translate("dsi.agents.dialog.title.tool-limit-tooltip")}
								size="xs"
							/>
						</div>
						<CustomTextField
							fullWidth
							className={styles.textfieldContainer__input}
							type="number"
							value={toolLimitState}
							variant="outlined"
							onChange={(e) => handleChangeToolLimit(e.target.value)}
						/>
					</div>
				</div>
			</div>
			<CustomDialog
				iconColor="var(--red-500)"
				iconModel={icon.faExclamationTriangle}
				open={openConfirmation}
				submitLabel={translate("common:btn.confirm")}
				subTitle={translate(`dsi.agents.warning.${(agent && "modification") || "creation"}.cancel`)}
				onClose={() => setOpenConfirmation(false)}
				onSubmit={handleDefinitiveClose}
			/>
		</CustomDialog>
	);
}
